import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { MdAddBox, MdDelete } from 'react-icons/md';
import { FloatChampInput } from '../../../ressources/functionJS/FloatChampInput';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { unixTimestampToDate } from '../../../ressources/functionJS/unixTimestampToDate';
import Bouton from '../../UI/Bouton/Bouton';
import DataGrid from '../../UI/DataGrid/DataGrid';
import FormulaireYScale from '../ProfilCapteur/TableauComposante/ModifierComposante/FormulaireYScale/FormulaireYScale';
import styles from './AddCapteurComposante.module.scss';
import FormulaireSelectionDateUnix from './SousComposantTable/SelectionDateForUnixTimestamp/FormulaireSelectionDateUnix';

// Column configuration
const SIMPLE_FIELDS = [
  'forumula',
  'prefix',
  'engineering_unit',
  'calibration_factors',
];
const NUMERIC_FIELDS = [
  'time_period',
  'measures_num',
  'serial_number',
  'coordinate_x',
  'coordinate_y',
  'coordinate_z',
  'time_refresh',
];
const DATE_FIELDS = ['start_formula', 'end_formula'];
const ALARM_LEVELS = [
  { level: 'high4', color: 'black' },
  { level: 'high3', color: 'red' },
  { level: 'high2', color: 'amber' },
  { level: 'high1', color: 'blue' },
  { level: 'low1', color: 'blue' },
  { level: 'low2', color: 'amber' },
  { level: 'low3', color: 'red' },
  { level: 'low4', color: 'black' },
];

// Factory functions
const createBaseColumn = (field, headerName, width = 150, type = null) => ({
  field,
  headerName,
  width,
  editable: true,
  sortable: true,
  align: 'center',
  headerAlign: 'center',
  ...(type && { type }),
});

const createActionColumn = (onDelete) => ({
  field: 'actions',
  headerName: checkMot('supprimer'),
  width: 100,
  editable: false,
  sortable: false,
  align: 'center',
  headerAlign: 'center',
  renderCell: (params) => (
    <Bouton cliquer={() => onDelete(params.row.component_id)}>
      <MdDelete size={20} />
    </Bouton>
  ),
});

const AddCapteurComposante = (props) => {
  const [componentList, setComponentList] = useState([]);
  const [increment, setIncrement] = useState(0);
  const [incrementSubComponent, setIncrementSubComponent] = useState(1);
  const { gererComposantes } = props;

  const supprimerComposante = useCallback(
    (composanteId) => {
      const newList = componentList.filter(
        (c) => c.component_id !== composanteId
      );
      setComponentList(newList);
      gererComposantes(newList);
    },
    [componentList, gererComposantes]
  );

  const columns = useMemo(
    () => [
      createActionColumn(supprimerComposante),
      createBaseColumn('component_id', checkMot('composante_id'), 180),
      createBaseColumn(
        'position_in_sensor',
        checkMot('position_in_sensor'),
        150,
        'number'
      ),
      // Simple fields
      ...SIMPLE_FIELDS.map((field) =>
        createBaseColumn(
          field,
          checkMot(field === 'engineering_unit' ? 'unite' : field),
          150
        )
      ),
      // Float field
      {
        ...createBaseColumn(
          'precision_decimal_notation',
          checkMot('resolution_decimales'),
          150,
          'number'
        ),
        valueFormatter: (value) => FloatChampInput(value),
      },
      // Date fields
      ...DATE_FIELDS.map((field) => ({
        ...createBaseColumn(field, checkMot(field), 180, 'number'),
        valueFormatter: (value) => unixTimestampToDate(value),
        renderEditCell: (params) => (
          <FormulaireSelectionDateUnix
            {...params}
            defaultValue={params.value}
            value={params.value}
          />
        ),
      })),
      // Y-scale
      {
        ...createBaseColumn('y_scale', checkMot('y_scale'), 150),
        valueFormatter: (value) => {
          if (!Array.isArray(value)) return '';
          return `[${value[0] ?? 'null'}, ${value[1] ?? 'null'}]`;
        },
        renderEditCell: (params) => (
          <FormulaireYScale
            {...params}
            defaultValue={params.value}
            value={params.value}
          />
        ),
      },
      // Numeric fields
      ...NUMERIC_FIELDS.map((field) =>
        createBaseColumn(field, checkMot(field), 130, 'number')
      ),
      // Alarm levels
      ...ALARM_LEVELS.map(({ level, color }) =>
        createBaseColumn(
          `alarmlevel_${level}`,
          checkMot(`alarmlevel_${level}_${color}`),
          130,
          'number'
        )
      ),
    ],
    [supprimerComposante]
  );

  const createNewComponent = useCallback(() => {
    const defaultId = `Nouveau composant n° ${increment}`;
    const newComponent = {
      id: `temp_${Date.now()}_${increment}`,
      component_id: defaultId,
      sensor_id: parseInt(props.sensor_id),
      position_in_sensor: incrementSubComponent,
      serial_number: null,
      coordinate_x: null,
      coordinate_y: null,
      coordinate_z: null,
      forumula: null,
      prefix: null,
      engineering_unit: null,
      precision_decimal_notation: null,
      alarmlevel_high4: null,
      alarmlevel_high3: null,
      alarmlevel_high2: null,
      alarmlevel_high1: null,
      alarmlevel_low1: null,
      alarmlevel_low2: null,
      alarmlevel_low3: null,
      alarmlevel_low4: null,
      start_formula: null,
      end_formula: null,
      calibration_factors: null,
      y_scale: [null, null],
      time_period: 2678400,
      measures_num: 10,
      time_refresh: null,
    };
    const newList = [...componentList, newComponent];
    setComponentList(newList);
    gererComposantes(newList);
    setIncrement(increment + 1);
    setIncrementSubComponent(incrementSubComponent + 1);
  }, [
    componentList,
    increment,
    incrementSubComponent,
    props.sensor_id,
    gererComposantes,
  ]);

  const handleRowUpdate = useCallback(
    (newRow) => {
      const newList = componentList.map((c) =>
        c.id === newRow.id ? newRow : c
      );
      setComponentList(newList);
      gererComposantes(newList);
      return newRow;
    },
    [componentList, gererComposantes]
  );

  return (
    <Fragment>
      <div className={styles.container}>
        <DataGrid
          rows={componentList}
          columns={columns}
          getRowId={(row) => row.id || row.component_id}
          editMode="cell"
          processRowUpdate={handleRowUpdate}
          pageSizeOptions={[10, 25, 50]}
          initialPageSize={10}
          disableRowSelectionOnClick
          autoHeight
        />
      </div>
      <button className={styles.btn_stylo} onClick={createNewComponent}>
        <MdAddBox />
        {checkMot('ajouter_une_composante')}
      </button>
    </Fragment>
  );
};

export default AddCapteurComposante;
